import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Users, Mail, ExternalLink, BookOpen, Award, 
  Target, FileText, Sparkles, Brain
} from 'lucide-react';

export default function EditorialBoardPage() {
  return (
    <div data-testid="editorial-board-page" className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <Badge className="bg-white/20 text-white border-0 mb-4">
            Équipe éditoriale
          </Badge>
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-4">
            Comité éditorial
          </h1>
          <p className="text-lg text-primary-foreground/90 max-w-3xl">
            L'équipe qui guide et accompagne le Journal de pratiques et recherches en psychothérapie.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {/* Editor-in-Chief */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold mb-6">Rédacteur en chef</h2>
          <Card className="overflow-hidden">
            <div className="md:flex">
              <div className="md:w-1/3 bg-primary/5 p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 border-4 border-primary/20">
                    <span className="text-4xl font-serif font-bold text-primary">VL</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold">Victor Lopez-Hernandez</h3>
                  <p className="text-muted-foreground">Rédacteur en chef</p>
                  <p className="text-xs text-muted-foreground mt-1">Psychosociologue & Thérapeute praticien TCC</p>
                  <p className="text-xs text-muted-foreground">Cabinet Alésia Thérapie</p>
                </div>
              </div>
              <CardContent className="md:w-2/3 p-8">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Biographie</h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      Psychosociologue et thérapeute praticien des TCC, Victor Lopez-Hernandez dirige 
                      le cabinet Alésia Thérapie. Titulaire d'un Master en sociologie du travail et des 
                      organisations (spécialisation en formation d'adultes, gestion de la formation et de 
                      la transition professionnelle) de l'Université Libre de Bruxelles, d'un Diplôme 
                      Universitaire « Compétences en psychiatrie et santé mentale » de l'Université Paris 
                      Cité, et d'une certification de praticien en thérapies comportementales et cognitives 
                      par l'E-FPP. Membre de l'AEMTC, il est lauréat du prix « Jeune chercheur » décerné 
                      par l'AEMTC pour la présentation d'un poster exposant un cas clinique de trouble 
                      panique associé à une maladie chronique. Passionné par l'alliance entre rigueur 
                      scientifique et innovation technologique en psychothérapie, il a fondé le JPRP pour 
                      encourager le partage d'expériences cliniques et la diffusion de pratiques basées 
                      sur les preuves.
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">TCC</Badge>
                    <Badge variant="secondary">Psychosociologie</Badge>
                    <Badge variant="secondary">Santé mentale</Badge>
                    <Badge variant="secondary">Innovation thérapeutique</Badge>
                    <Badge variant="secondary">AEMTC</Badge>
                  </div>

                  <div className="flex flex-wrap gap-4 pt-4">
                    <a 
                      href="https://orcid.org/0009-0007-1950-7812"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <img src="https://orcid.org/assets/vectors/orcid.logo.icon.svg" alt="ORCID" className="w-4 h-4" />
                      0009-0007-1950-7812
                    </a>
                    <a 
                      href="mailto:victor.loher@vmlh-tcc.fr"
                      className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Mail className="w-4 h-4" />
                      victor.loher@vmlh-tcc.fr
                    </a>
                  </div>
                </div>
              </CardContent>
            </div>
          </Card>
        </section>

        {/* Mission du comité */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold mb-6">Mission du comité éditorial</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Target className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Évaluation rigoureuse</h3>
                <p className="text-sm text-muted-foreground">
                  Chaque manuscrit fait l'objet d'une lecture attentive, centrée sur la pertinence 
                  et la clarté du propos.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="font-semibold mb-2">Accompagnement bienveillant</h3>
                <p className="text-sm text-muted-foreground">
                  Les auteurs sont accompagnés tout au long du processus d'évaluation avec 
                  réactivité et constructivité.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-accent/30 flex items-center justify-center mx-auto mb-4">
                  <Award className="w-7 h-7 text-accent-foreground" />
                </div>
                <h3 className="font-semibold mb-2">Excellence scientifique</h3>
                <p className="text-sm text-muted-foreground">
                  Garantir la qualité et la rigueur des publications au service de la 
                  communauté scientifique.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Processus d'évaluation */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold mb-6">Processus d'évaluation</h2>
          <Card>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                  { step: 1, title: 'Soumission', desc: 'L\'auteur soumet son manuscrit via la plateforme' },
                  { step: 2, title: 'Lecture initiale', desc: 'Vérification de la conformité et de la pertinence' },
                  { step: 3, title: 'Évaluation', desc: 'Examen approfondi par le comité éditorial' },
                  { step: 4, title: 'Décision', desc: 'Publication, révision ou refus motivé' }
                ].map((item) => (
                  <div key={item.step} className="text-center">
                    <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 text-lg font-bold">
                      {item.step}
                    </div>
                    <h3 className="font-semibold mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  <strong>Délai moyen d'évaluation :</strong> 4 à 6 semaines après soumission. 
                  Les auteurs sont informés à chaque étape du processus.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Rejoindre le comité */}
        <section className="mb-12">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-8">
              <div className="md:flex items-center justify-between gap-8">
                <div>
                  <h2 className="font-serif text-2xl font-semibold mb-2">Rejoindre le comité</h2>
                  <p className="text-muted-foreground">
                    Vous êtes chercheur ou praticien expérimenté en psychothérapie ? 
                    Le JPRP recherche des évaluateurs pour rejoindre son comité éditorial.
                  </p>
                </div>
                <div className="mt-4 md:mt-0">
                  <a href="mailto:victor.loher@vmlh-tcc.fr?subject=Candidature comité éditorial JPRP">
                    <Button size="lg" className="gap-2">
                      <Mail className="w-4 h-4" />
                      Postuler
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Ligne éditoriale */}
        <section>
          <h2 className="font-serif text-2xl font-semibold mb-6">Ligne éditoriale</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Brain className="w-5 h-5 text-primary" />
                  Approche TCC
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  La revue s'inscrit dans la tradition des thérapies comportementales et cognitives 
                  tout en explorant de nouvelles approches telles que le transdiagnostique et 
                  l'intégration des technologies numériques.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-secondary" />
                  Innovation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Le journal accueille avec enthousiasme les travaux intégrant les nouvelles 
                  technologies : réalité virtuelle, dispositifs numériques, intelligence artificielle, 
                  interventions à distance.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  Interdisciplinarité
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  À la croisée de la psychologie, de la sociologie, de la psychiatrie et des 
                  sciences cognitives, notre journal s'adresse à tous ceux qui cherchent à allier 
                  rigueur scientifique et humanité clinique.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-secondary" />
                  Diversité des formats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Recherches empiriques, études de cas, revues de littérature, notes de pratique, 
                  protocoles de recherche et présentations d'innovations technologiques sont 
                  les bienvenus.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
}
